import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trpc } from "../lib/trpc.js";
import { Input } from "../components/ui/Input.js";
import { NavActions } from "../components/NavActions.js";
import { Button } from "../components/ui/Button.js";
import { LocationAutocomplete } from "../components/ui/LocationAutocomplete.js";

type ClubTypeFilter = "IN_PERSON" | "ONLINE" | "BOTH";

export function ClubSearch() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<ClubTypeFilter | undefined>(undefined);
  const [cityInput, setCityInput] = useState("");
  const [cityFilter, setCityFilter] = useState<string | undefined>(undefined);
  const [selectedClubId, setSelectedClubId] = useState<string | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce name query by 300ms to avoid firing on every keystroke
  const queryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
    queryDebounceRef.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => { if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current); };
  }, [query]);

  // Debounce city input by 300ms
  const cityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = setTimeout(() => {
      setCityFilter(cityInput.trim() || undefined);
    }, 300);
    return () => { if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current); };
  }, [cityInput]);

  // Only make the query when debounced input >= 3 chars
  const searchQuery = trpc.clubs.search.useQuery(
    { query: debouncedQuery, type: typeFilter, city: cityFilter },
    { enabled: debouncedQuery.length >= 3 }
  );

  // Show loading while user is still typing or query is in-flight
  const isLoading = query.length >= 3 && (debouncedQuery !== query.trim() || searchQuery.isPending);

  // Show results only if debounced query is active and we have data
  const results = debouncedQuery.length >= 3 ? searchQuery.data : undefined;
  const isError = debouncedQuery.length >= 3 && searchQuery.isError;

  const utils = trpc.useUtils();
  const requestJoin = trpc.joinRequests.request.useMutation({
    onSuccess: () => {
      setSelectedClubId(null);
      utils.clubs.search.invalidate({ query: debouncedQuery, type: typeFilter });
    },
  });

  const handleJoinClick = (e: React.MouseEvent, clubId: string) => {
    e.stopPropagation();
    setSelectedClubId(clubId);
    requestJoin.mutate({ clubId });
  };

  const handleCardClick = (club: any) => {
    if (!club.isMember && !club.hasPendingRequest) {
      // Show join request UI instead of navigating
      return;
    }
    // Navigate if member or no pending request
    navigate(`/clubs/${club.id}`);
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 md:px-10"
        style={{
          background: "oklch(97% 0.014 82 / 0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <span
          className="font-serif text-lg font-semibold tracking-tight"
          style={{ color: "var(--color-foreground)" }}
        >
          {t("nav.logo")}
        </span>
        <NavActions />
      </header>

      {/* Main */}
      <main className="mx-auto max-w-2xl px-6 py-10 md:px-10">
        {/* Page title row */}
        <div
          className="flex items-end justify-between mb-2"
          style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both" }}
        >
          <div>
            <button
              onClick={() => navigate("/dashboard")}
              className="text-xs font-medium uppercase tracking-widest mb-1"
              style={{
                color: "var(--color-accent)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0
              }}
            >
              {t("nav.backToDashboard")}
            </button>
            <h1
              className="font-serif font-semibold"
              style={{ fontSize: "1.75rem", letterSpacing: "-0.02em", color: "var(--color-foreground)" }}
            >
              {t("clubSearch.title")}
            </h1>
          </div>
        </div>

        {/* Thin rule */}
        <div
          className="mb-6 mt-4 h-px"
          style={{
            background: "linear-gradient(to right, var(--color-accent), transparent)",
            opacity: 0.4,
            animation: "fade-in 0.5s ease-out 0.1s both",
          }}
        />

        {/* Search input */}
        <div className="mb-4">
          <Input
            placeholder={t("clubSearch.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ animation: "slide-up-sm 0.3s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both" }}
          />
        </div>

        {/* Type filter chips */}
        <div className="flex flex-wrap gap-2 mb-3" style={{ animation: "slide-up-sm 0.3s cubic-bezier(0.16, 1, 0.3, 1) 0.15s both" }}>
          {([undefined, "IN_PERSON", "ONLINE", "BOTH"] as (ClubTypeFilter | undefined)[]).map((type) => {
            const label = type ? t(`clubType.filter.${type}`) : t("clubType.filter.all");
            const active = typeFilter === type;
            return (
              <button
                key={type ?? "all"}
                onClick={() => setTypeFilter(type)}
                className="px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer"
                style={{
                  background: active ? "var(--color-accent)" : "var(--color-secondary)",
                  color: active ? "white" : "var(--color-muted-foreground)",
                  border: active ? "1px solid var(--color-accent)" : "1px solid var(--color-border)",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* City filter input */}
        <div className="mb-6" style={{ animation: "slide-up-sm 0.3s cubic-bezier(0.16, 1, 0.3, 1) 0.18s both" }}>
          <LocationAutocomplete
            type="city"
            value={cityInput}
            onChange={setCityInput}
            placeholder={t("clubLocation.filterPlaceholder")}
            inputStyle={{ height: "2.625rem", borderRadius: "0.5rem" }}
          />
        </div>

        {/* Results section */}
        <div className="space-y-3">
          {isLoading && (
            <div className="flex items-center gap-2 py-8 justify-center">
              <span
                className="h-4 w-4 rounded-full border-2 border-current border-t-transparent"
                style={{ color: "var(--color-accent)", animation: "spin 0.7s linear infinite" }}
              />
              <span className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>
                {t("common.loading")}
              </span>
            </div>
          )}

          {!isLoading && isError && (
            <div
              className="text-center py-14 rounded-xl"
              style={{
                background: "var(--color-card)",
                border: "1px dashed var(--color-border-strong)",
              }}
            >
              <p
                className="font-serif italic text-base"
                style={{ color: "var(--color-muted-foreground)" }}
              >
                {t("clubSearch.searchError")}
              </p>
            </div>
          )}

          {!isLoading && !isError && results && results.length === 0 && (
            <div
              className="text-center py-14 rounded-xl"
              style={{
                background: "var(--color-card)",
                border: "1px dashed var(--color-border-strong)",
              }}
            >
              <p
                className="font-serif italic text-base"
                style={{ color: "var(--color-muted-foreground)" }}
              >
                {t("clubSearch.noResults", { query: debouncedQuery })}
              </p>
            </div>
          )}

          {!isLoading && !isError && results && results.length > 0 && (
            results.map((club, i) => (
              <div
                key={club.id}
                className="book-spine w-full rounded-xl transition-all duration-150 group"
                style={{
                  background: "var(--color-card)",
                  border: "1px solid var(--color-border)",
                  boxShadow: "var(--shadow-sm)",
                  animation: `slide-up-sm 0.3s cubic-bezier(0.16, 1, 0.3, 1) ${0.05 + i * 0.06}s both`,
                }}
              >
                <button
                  className="w-full text-left px-5 py-4 transition-all duration-150 hover:shadow-[var(--shadow-md)]"
                  onClick={() => handleCardClick(club)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 pl-3 flex-1">
                      <p
                        className="font-serif font-medium text-base truncate"
                        style={{ color: "var(--color-foreground)", letterSpacing: "-0.01em" }}
                      >
                        {club.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                          {t("dashboard.memberCount", { count: club.memberCount })}
                        </p>
                        {club.description && (
                          <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                            {club.description}
                          </p>
                        )}
                        {(club.locationCity || club.locationCountry) && (
                          <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                            {[club.locationCity, club.locationCountry].filter(Boolean).join(", ")}
                          </p>
                        )}
                        {club.type && (
                          <span
                            className="text-xs px-2 py-1 rounded-full"
                            style={{
                              background: "var(--color-muted)",
                              color: "var(--color-muted-foreground)",
                            }}
                          >
                            {t(`clubType.${club.type}`)}
                          </span>
                        )}
                        {club.isMember && (
                          <span
                            className="text-xs px-2 py-1 rounded-sm"
                            style={{
                              background: "var(--color-accent-subtle)",
                              color: "var(--color-accent-subtle-foreground)",
                            }}
                          >
                            {t("clubSearch.alreadyMember")}
                          </span>
                        )}
                        {club.hasPendingRequest && (
                          <span
                            className="text-xs px-2 py-1 rounded-sm"
                            style={{
                              background: "var(--color-accent-subtle)",
                              color: "var(--color-accent-subtle-foreground)",
                            }}
                          >
                            {t("joinRequest.pending")}
                          </span>
                        )}
                      </div>
                    </div>
                    {!club.isMember && !club.hasPendingRequest && (
                      <Button
                        size="sm"
                        loading={requestJoin.isPending && selectedClubId === club.id}
                        disabled={requestJoin.isPending}
                        onClick={(e) => handleJoinClick(e, club.id)}
                        className="flex-shrink-0 ml-3"
                      >
                        {t("joinRequest.request")}
                      </Button>
                    )}
                    {!club.isMember && !club.hasPendingRequest && (
                      <span
                        className="ml-3 text-xs opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        style={{ color: "var(--color-muted-foreground)" }}
                        aria-hidden
                      >
                        {t("dashboard.openClub")}
                      </span>
                    )}
                    {(club.isMember || club.hasPendingRequest) && (
                      <span
                        className="ml-3 text-xs opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        style={{ color: "var(--color-muted-foreground)" }}
                        aria-hidden
                      >
                        {t("dashboard.openClub")}
                      </span>
                    )}
                  </div>
                </button>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
